import { useCallback, useMemo } from 'react';

import type { CalendarEvent } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';

export type EventAvatarData = {
  imageUri: string | null;
  color: string;
  name: string;
};

type UseEventAvatarResolverParams = {
  familyMembers: FamilyMember[];
  calendarMappings: CalendarMemberMapping[];
  deviceCalendars: DeviceCalendar[];
  defaultColor: string;
};

/**
 * Hook that resolves event avatar data based on calendar-to-member mappings.
 *
 * Resolution logic:
 * 1. Look up event's deviceCalendarId in mappings → get memberIds
 * 2. Find first matching family member → return their profilePicture, color, name initial
 * 3. If no mapping, find device calendar → return first letter of calendar title
 * 4. Use defaultColor if no member color available
 */
export function useEventAvatarResolver({
  familyMembers,
  calendarMappings,
  deviceCalendars,
  defaultColor,
}: UseEventAvatarResolverParams) {
  const memberMap = useMemo(() => {
    const map = new Map<string, FamilyMember>();
    for (const member of familyMembers) {
      map.set(member.id, member);
    }
    return map;
  }, [familyMembers]);

  const mappingMap = useMemo(() => {
    const map = new Map<string, CalendarMemberMapping>();
    for (const mapping of calendarMappings) {
      map.set(mapping.calendarId, mapping);
    }
    return map;
  }, [calendarMappings]);

  const calendarMap = useMemo(() => {
    const map = new Map<string, DeviceCalendar>();
    for (const calendar of deviceCalendars) {
      map.set(calendar.id, calendar);
    }
    return map;
  }, [deviceCalendars]);

  const resolveEventAvatar = useCallback(
    (event: CalendarEvent): EventAvatarData => {
      const mapping = mappingMap.get(event.deviceCalendarId);

      if (mapping && mapping.memberIds.length > 0) {
        const member = memberMap.get(mapping.memberIds[0]);
        if (member) {
          return {
            imageUri: member.profilePicture,
            color: member.color,
            name: member.name.charAt(0),
          };
        }
      }

      const calendar = calendarMap.get(event.deviceCalendarId);
      if (calendar) {
        const firstChar = calendar.title.charAt(0).toUpperCase();
        return {
          imageUri: null,
          color: defaultColor,
          name: firstChar || '?',
        };
      }

      return {
        imageUri: null,
        color: defaultColor,
        name: event.title.charAt(0).toUpperCase() || '?',
      };
    },
    [mappingMap, memberMap, calendarMap, defaultColor],
  );

  return { resolveEventAvatar };
}
